import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import {
  catchError,
  concat,
  last,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';

import { APIResponse, BasicResponse } from '../_types/api-responses.types';
import {
  NotificationCounts,
  NotificationItem,
  NotificationListRequest,
  NotificationPage,
  UpdateNotificationsRequest,
} from '../_types/notifications.types';
import { Globals } from '../globals';

/** The most ids one PATCH accepts. */
const MAX_UPDATE_IDS = 500;

/** The viewer's in-app notifications inbox. */
@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly baseUrl = `${environment.apiUrl}api/communicator/v1/notifications`;

  private readonly latestCounts = signal<NotificationCounts>({
    all: 0,
    unread: 0,
    archived: 0,
  });

  /** The last counts the backend gave, refreshed by every call that moves them. */
  readonly totals = this.latestCounts.asReadonly();

  /** The sidebar badge. */
  readonly unread = computed(() => this.latestCounts().unread);

  constructor(
    private httpClient: HttpClient,
    private globals: Globals
  ) {}

  private get headers(): HttpHeaders {
    return new HttpHeaders()
      .set('content-type', 'application/json')
      .set('Access-Token', this.globals.getUser().token);
  }

  list(
    request: NotificationListRequest
  ): Observable<APIResponse<NotificationPage>> {
    return this.httpClient
      .get<APIResponse<NotificationPage>>(this.baseUrl, {
        headers: this.headers,
        // An empty search or category is left out rather than sent blank.
        params: Object.fromEntries(
          Object.entries(request).filter(([, v]) => v !== undefined && v !== '')
        ),
      })
      .pipe(tap((res) => this.latestCounts.set(res.data.counts)));
  }

  counts(): Observable<APIResponse<NotificationCounts>> {
    return this.httpClient
      .get<
        APIResponse<NotificationCounts>
      >(`${this.baseUrl}/counts`, { headers: this.headers })
      .pipe(tap((res) => this.latestCounts.set(res.data)));
  }

  /** Marks the notification read, but answers with its state from before. */
  get(id: number): Observable<APIResponse<NotificationItem>> {
    return this.recounted(
      this.httpClient.get<APIResponse<NotificationItem>>(
        `${this.baseUrl}/${id}`,
        { headers: this.headers }
      )
    );
  }

  /** Sends the ids in batches the API accepts, one after the other. */
  update(
    body: UpdateNotificationsRequest
  ): Observable<APIResponse<BasicResponse>> {
    const batches: Observable<APIResponse<BasicResponse>>[] = [];
    for (let i = 0; i < body.ids.length; i += MAX_UPDATE_IDS) {
      batches.push(
        this.httpClient.patch<APIResponse<BasicResponse>>(
          this.baseUrl,
          { ...body, ids: body.ids.slice(i, i + MAX_UPDATE_IDS) },
          { headers: this.headers }
        )
      );
    }
    return this.recounted(concat(...batches).pipe(last()));
  }

  /** Every unread notification not archived. */
  readAll(): Observable<APIResponse<BasicResponse>> {
    return this.recounted(
      this.httpClient.post<APIResponse<BasicResponse>>(
        `${this.baseUrl}/read-all`,
        null,
        { headers: this.headers }
      )
    );
  }

  /**
   * Answers once the counts are refetched too, so the caller's change and the
   * new counts land together. A failed recount leaves the old counts.
   */
  private recounted<T>(change: Observable<T>): Observable<T> {
    return change.pipe(
      switchMap((res) =>
        this.counts().pipe(
          map(() => res),
          catchError(() => of(res))
        )
      )
    );
  }
}
